import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons, provideNgIconLoader } from '@ng-icons/core';
import { lucideCircleAlert, lucideCircleHelp, lucideHouse, lucidePhone } from '@ng-icons/lucide';

import { FocusOnlyRadioGroupDirective } from 'app/home/common/directives/focus-only-radio-group.directive';
import { ReadonlyRadioDirective } from 'app/home/common/directives/readonly-radio.directive';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, WithLockableFields } from 'app/home/create-quote/form-locking';
import { LockedQuoteAlertComponent } from 'app/home/create-quote/form-locking/locked-quote-alert.component';
import { FoundationTypeQuoteFormInput, FoundationTypeValue } from 'app/home/create-quote/forms/property-details/foundation/foundation.types';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { ContactDialogService } from 'app/home/create-quote/services/contact-dialog/contact-dialog.service';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { QuoteFormOptionsStore } from 'app/home/global-store/quote-form-options';
import * as cc from 'app/shared/constants/contact-constants';
import { PhonePipe } from 'app/shared/pipes/phone-number/phone.pipe';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';

@Component({
  selector: 'app-foundation',
  imports: [FormCardComponent, NgIconComponent, ReactiveFormsModule, PhonePipe, LockedQuoteAlertComponent, ReadonlyRadioDirective, FocusOnlyRadioGroupDirective],
  templateUrl: './foundation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    provideIcons({ lucideCircleAlert, lucideCircleHelp, lucideHouse, lucidePhone }),
    provideNgIconLoader((filename) => {
      const http = inject(HttpClient);
      return http.get(`/assets/icons/${filename}`, { responseType: 'text' });
    }),
  ],
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/property-details/foundation',
  isAutoAdvanced: true,
})
export class QuoteFormFoundationTypeComponent extends BaseQuoteForm<FoundationTypeQuoteFormInput> implements OnInit, Lockable {
  private optionsStore = inject(QuoteFormOptionsStore);
  private featureFlagSignalsService = inject(FeatureFlagSignalsService);
  public isAutoAdvanceEnabled = this.featureFlagSignalsService.isAutoAdvanceEnabled;
  protected contactDialogService = inject(ContactDialogService);
  public formDataIn: FoundationTypeQuoteFormInput = this.quoteDraftStore.foundationTypeQuoteFormSelector();

  public foundationTypeOptions = this.optionsStore.foundationTypeQuoteFormOptionsSelector();

  public formDefinition = {
    foundationType: new FormControl<FoundationTypeValue | undefined>(undefined, Validators.required),
  };
  public foundationTypeForm = new FormGroup(this.formDefinition);

  public salesPhoneNumber = cc.PHONE_NUMBERS.sales;
  public salesPhoneHoursFormatted = cc.PHONE_HOURS_FORMATTED.sales;

  public override autoAdvanceControlRef = this.foundationTypeForm.controls.foundationType;

  public ngOnInit() {
    if (this.formDataIn && this.isFieldLocked('foundationType')) {
      this.foundationTypeForm.controls.foundationType.setValue(this.formDataIn.foundationType);
    }
  }

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  private getFormValues(): Pick<QuoteDraftState, 'foundationType'> {
    const { controls } = this.foundationTypeForm;
    const formValues = {
      foundationType: controls.foundationType.value ?? undefined,
    };

    return formValues;
  }

  public getFormControls() {
    return this.foundationTypeForm.controls;
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.foundationTypeForm.markAllAsTouched();
    if (this.foundationTypeForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        super.saveFormData(this.getFormValues());
      }
    }
  }

  public get hasError() {
    if (this.isAutoAdvanceEnabled()) {
      return false;
    }
    const control = this.foundationTypeForm.get('foundationType');
    return !!control?.invalid && !!control?.touched;
  }
}
